import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { DeliveryChannel, Digest, Preference, Profile, Topic } from "@z-suite/shared";
import { supabase } from "./supabase.js";

/** Bugünün tarihi, TRT'ye göre "YYYY-MM-DD". */
export function todayTRT(): string {
  return new Date().toLocaleDateString("en-CA", { timeZone: "Europe/Istanbul" });
}

/* ── Topics (paylaşımlı taksonomi) ──────────────────────────────────────── */
export function useTopics() {
  return useQuery({
    queryKey: ["topics"],
    queryFn: async (): Promise<Topic[]> => {
      const { data, error } = await supabase
        .from("topics")
        .select("*")
        .eq("is_active", true)
        .order("category")
        .order("label");
      if (error) throw error;
      return data as Topic[];
    },
    staleTime: 1000 * 60 * 10,
  });
}

/* ── Profile ────────────────────────────────────────────────────────────── */
export function useProfile(userId: string | undefined) {
  return useQuery({
    queryKey: ["profile", userId],
    enabled: Boolean(userId),
    queryFn: async (): Promise<Profile | null> => {
      const { data, error } = await supabase
        .from("profiles")
        .select("*")
        .eq("id", userId!)
        .maybeSingle();
      if (error) throw error;
      return data as Profile | null;
    },
  });
}

/* ── Preferences (kullanıcıya özel, topic join'li) ──────────────────────── */
export type PreferenceWithTopic = Preference & { topic: Topic | null };

export function usePreferences(userId: string | undefined) {
  return useQuery({
    queryKey: ["preferences", userId],
    enabled: Boolean(userId),
    queryFn: async (): Promise<PreferenceWithTopic[]> => {
      const { data, error } = await supabase
        .from("preferences")
        .select("*, topic:topics(*)")
        .eq("user_id", userId!)
        .order("created_at", { ascending: true });
      if (error) throw error;
      return (data ?? []) as PreferenceWithTopic[];
    },
  });
}

/* ── Digests (kullanıcıya özel brifingler) ──────────────────────────────── */
export function useTodayDigest(userId: string | undefined) {
  return useQuery({
    queryKey: ["digest", userId, todayTRT()],
    enabled: Boolean(userId),
    queryFn: async (): Promise<Digest | null> => {
      const { data, error } = await supabase
        .from("digests")
        .select("*")
        .eq("user_id", userId!)
        .eq("digest_date", todayTRT())
        .maybeSingle();
      if (error) throw error;
      return data as Digest | null;
    },
  });
}

export function useDigestByDate(userId: string | undefined, date: string | undefined) {
  return useQuery({
    queryKey: ["digest", userId, date],
    enabled: Boolean(userId && date),
    queryFn: async (): Promise<Digest | null> => {
      const { data, error } = await supabase
        .from("digests")
        .select("*")
        .eq("user_id", userId!)
        .eq("digest_date", date!)
        .maybeSingle();
      if (error) throw error;
      return data as Digest | null;
    },
  });
}

/** Geçmiş brifingler (tarih listesi, timeline için). */
export function useDigestList(userId: string | undefined) {
  return useQuery({
    queryKey: ["digest-list", userId],
    enabled: Boolean(userId),
    queryFn: async (): Promise<Pick<Digest, "id" | "digest_date" | "created_at">[]> => {
      const { data, error } = await supabase
        .from("digests")
        .select("id, digest_date, created_at")
        .eq("user_id", userId!)
        .order("digest_date", { ascending: false });
      if (error) throw error;
      return (data ?? []) as Pick<Digest, "id" | "digest_date" | "created_at">[];
    },
  });
}

/* ── Onboarding kaydı: profil + seçilen konular ─────────────────────────── */
export interface OnboardingPayload {
  userId: string;
  full_name: string;
  company: string;
  role: string;
  delivery_time: string; // "07:00"
  delivery_channels: DeliveryChannel[];
  topicIds: string[];
}

export function useSaveOnboarding() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (p: OnboardingPayload) => {
      // 1) Profili güncelle (trigger satırı zaten oluşturmuş olabilir → upsert)
      const { error: pErr } = await supabase.from("profiles").upsert({
        id: p.userId,
        full_name: p.full_name,
        company: p.company,
        role: p.role,
        delivery_time: p.delivery_time,
        delivery_channels: p.delivery_channels,
      });
      if (pErr) throw pErr;

      // 2) Konuları yaz (önce mevcutları temizle → idempotent onboarding)
      await supabase.from("preferences").delete().eq("user_id", p.userId);
      if (p.topicIds.length > 0) {
        const { error: prefErr } = await supabase.from("preferences").insert(
          p.topicIds.map((topic_id) => ({ user_id: p.userId, topic_id, weight: 3 })),
        );
        if (prefErr) throw prefErr;
      }
    },
    onSuccess: (_d, p) => {
      // Yarışı kapat: yönlendirmeden ÖNCE önbelleği anında güncelle, böylece
      // RequireOnboarding bayat (company=null) veriyle kullanıcıyı geri atmaz.
      qc.setQueryData(["profile", p.userId], (old: Profile | null | undefined) => ({
        ...(old ?? {}),
        id: p.userId,
        full_name: p.full_name,
        company: p.company,
        role: p.role,
        delivery_time: p.delivery_time,
        delivery_channels: p.delivery_channels,
      }));
      qc.invalidateQueries({ queryKey: ["profile", p.userId] });
      qc.invalidateQueries({ queryKey: ["preferences", p.userId] });
    },
  });
}

/* ── Ayarlar (/settings ekranı) ─────────────────────────────────────────── */
export interface SettingsPatch {
  full_name?: string;
  company?: string;
  role?: string;
  delivery_time?: string;
  delivery_channels?: DeliveryChannel[];
  slack_webhook_url?: string | null;
}

export function useUpdateSettings(userId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (patch: SettingsPatch) => {
      const { error } = await supabase.from("profiles").update(patch).eq("id", userId);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["profile", userId] }),
  });
}

/* ── Tek tek tercih yönetimi (/preferences ekranı) ──────────────────────── */
export function useAddPreference(userId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { topic_id?: string; keyword?: string; weight?: number }) => {
      const { error } = await supabase.from("preferences").insert({
        user_id: userId,
        topic_id: input.topic_id ?? null,
        keyword: input.keyword ?? null,
        weight: input.weight ?? 3,
      });
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["preferences", userId] }),
  });
}

export function useRemovePreference(userId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("preferences").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["preferences", userId] }),
  });
}

export function useUpdatePreferenceWeight(userId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, weight }: { id: string; weight: number }) => {
      const { error } = await supabase.from("preferences").update({ weight }).eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["preferences", userId] }),
  });
}
